import { Component, type ReactNode } from "react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";

interface ErrorInfo {
    componentStack?: string;
}

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    name?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
        console.error(
            `ErrorBoundary${this.props.name ? ` (${this.props.name})` : ""} caught:`,
            error,
            errorInfo,
        );
    }

    handleDismiss = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleReload = () => {
        window.location.reload();
    };

    override render() {
        const { hasError, error } = this.state;
        const { children, fallback, name } = this.props;

        if (hasError) {
            if (fallback) {
                return fallback;
            }

            return (
                <Dialog
                    open={true}
                    onClose={this.handleDismiss}
                    title="Something went wrong"
                    description={`An unexpected error occurred${name ? ` in ${name}` : ""}. Please try reloading the page.`}
                    size="lg"
                >
                    <div className="bg-(--color-bg-muted) border border-(--color-border) rounded-lg p-3 mb-4 max-h-32 overflow-auto">
                        <code className="text-xs text-red-500 font-mono whitespace-pre-wrap break-all">
                            {error?.name}: {error?.message}
                        </code>
                    </div>

                    <div className="flex gap-3 justify-end">
                        <Button variant="secondary" onClick={this.handleDismiss}>
                            Dismiss
                        </Button>
                        <Button variant="primary" onClick={this.handleReload}>
                            Reload page
                        </Button>
                    </div>
                </Dialog>
            );
        }

        return children;
    }
}
